import { useDispatch, useSelector } from 'react-redux'
import submissionSelectors from '../../../selectors/submission.selectors'
import { submissionActions } from '../../../slices/submission.slice'
import { useFetchQuestionsQuery } from '../../question.api'
import textSubmissionDrawerSelectors from '../../../selectors/text-submission-drawer.selectors'
import { useEffect, useState } from 'react'
import { textSubmissionDrawerActions, ViewModes } from '../../../slices/text-submission-drawer.slice'
import useSubmissionGroups from '../../../../pages/assignment/sections/submissions/submission-groups/useSubmissionGroups'
import baseApi from '../../base.api'
import { useParams } from 'react-router'
import { useVersionId } from '../../../../utils/parseURL.helper'
import { useFetchTextSubmissionDetailsQuery } from '../../submission.api'
import { isNil, isNumber } from 'lodash'
import { getQuestionById } from '../selectors/question.api.selectors'
import { GradingModes } from '../../../../pages/assignment/components/text-submission-drawer/header/GradingModeSelect'
import userSelectors from '../../../selectors/user.selectors'
import { FLAGGED_PARAM, FOCUS_QUESTION_ID_PARAM, OPEN_SUBMISSION_ID_PARAM } from '../../../../constants/query-params'
import useQueryParamSetter from '../../../../hooks/useQueryParamSetter'
import useQueryParams from '../../../../hooks/useQueryParams'
import useQueryParam from '../../../../hooks/useQueryParam'
import { ExplainabilityStatuses } from '../../../../constants/explainability-statuses'
import assignmentSelectors from '../../../selectors/assignment.selectors'
import { flatten } from '../../../../utils/array.helper'

const prefetchSubmissions = (dispatch, submissionIds) =>
    submissionIds.map(id => dispatch(baseApi.endpoints.fetchTextSubmissionDetails.initiate({ submissionId: id })))

const getNextSubmissionIds = (group, currentIndex, numberOfSubmissionsToGet) => {
    if (!group || currentIndex === -1) return []
    const nextSubmissions = group.submissions.slice(currentIndex + 1, currentIndex + 1 + numberOfSubmissionsToGet)
    return nextSubmissions.map(submission => submission.id)
}

const getPreviousSubmissionIds = (group, currentIndex, numberOfSubmissionsToGet) => {
    if (!group || currentIndex === -1) return []
    const offset = Math.max(0, currentIndex - numberOfSubmissionsToGet)
    return group.submissions
        .slice(offset, currentIndex)
        .map(submission => submission.id)
        .reverse()
}

const getUnapprovedQuestions = (questions, answers, questionIdToSkip, userId, isOwner) => {
    if (!questions || !answers) return []

    return questions.filter(question => {
        if (question.id === questionIdToSkip) return false
        const answer = answers.find(answer => answer.questionId === question.id)
        return !answer?.approveStatus && (isNil(answer?.assigneeId) || answer.assigneeId === userId || isOwner)
    })
}

export const useTextSubmissionNavigation = () => {
    const dispatch = useDispatch()
    const openedSubmissionId = useSelector(submissionSelectors.openedSubmissionId)
    const groupsAtSubmissionOpenMoment = useSelector(submissionSelectors.groupsAtSubmissionOpenMoment)
    const gradingMode = useSelector(textSubmissionDrawerSelectors.gradingMode)
    const userId = useSelector(userSelectors.id)
    const isOwner = useSelector(assignmentSelectors.isOwner)

    const { token, layerId: questionIdParam } = useParams()
    const questionId = Number(questionIdParam)
    const versionId = useVersionId()

    const { groups: currentGroups } = useSubmissionGroups()
    const groups = groupsAtSubmissionOpenMoment || currentGroups

    const { data: questions } = useFetchQuestionsQuery({ token, versionId })

    const { data: submission } = useFetchTextSubmissionDetailsQuery({ submissionId: openedSubmissionId })

    const getCurrentGroup = () =>
        groups?.find(group => group.submissions.some(submission => submission.id === openedSubmissionId))

    const getSubmissionIndexInGroup = group =>
        group?.submissions.findIndex(submission => submission.id === openedSubmissionId) ?? -1

    const checkIfSubmissionExists = (group, offset) => {
        if (!group) return false
        const currentDrawerSubmissionIndex = getSubmissionIndexInGroup(group)
        if (currentDrawerSubmissionIndex === -1) return false
        return Boolean(group.submissions[currentDrawerSubmissionIndex + offset])
    }

    const checkIfPreviousSubmissionExists = group => checkIfSubmissionExists(group, -1)
    const checkIfNextSubmissionExists = group => checkIfSubmissionExists(group, 1)

    const navigateToPreviousSubmission = () => {
        const currentGroup = getCurrentGroup()
        if (!currentGroup) return
        const currentDrawerSubmissionIndex = getSubmissionIndexInGroup(currentGroup)

        if (checkIfPreviousSubmissionExists(currentGroup)) {
            const previousThreeSubmissionsIds = getPreviousSubmissionIds(currentGroup, currentDrawerSubmissionIndex, 3)
            dispatch(submissionActions.openSubmission({ id: previousThreeSubmissionsIds[0] }))

            if (isNumber(questionId)) dispatch(textSubmissionDrawerActions.setFocusedQuestionId(questionId))

            prefetchSubmissions(dispatch, previousThreeSubmissionsIds)
        }
    }

    const navigateToNextSubmission = () => {
        const currentGroup = getCurrentGroup()
        if (!currentGroup) return
        const currentDrawerSubmissionIndex = getSubmissionIndexInGroup(currentGroup)
        if (checkIfNextSubmissionExists(currentGroup)) {
            const nextThreeSubmissionsIds = getNextSubmissionIds(currentGroup, currentDrawerSubmissionIndex, 3)
            dispatch(submissionActions.openSubmission({ id: nextThreeSubmissionsIds[0] }))

            if (isNumber(questionId)) dispatch(textSubmissionDrawerActions.setFocusedQuestionId(questionId))

            prefetchSubmissions(dispatch, nextThreeSubmissionsIds)
        }
    }

    const navigateToNextUnreviewedAnswer = ({ lastReviewedQuestionId }) => {
        if (gradingMode === GradingModes.MANUAL) return

        const openedQuestion = getQuestionById(questions, questionId)
        const openedQuestionParent = getQuestionById(questions, openedQuestion?.parentId)
        const flattenedQuestions = flatten(questions, { excludeParents: true })
        const unapprovedQuestions = getUnapprovedQuestions(
            flattenedQuestions,
            submission.answers,
            lastReviewedQuestionId,
            userId,
            isOwner,
        )
        const unapprovedSiblingQuestions = getUnapprovedQuestions(
            openedQuestionParent?.children,
            submission.answers,
            lastReviewedQuestionId,
            userId,
            isOwner,
        )

        const gradingModeHandlers = {
            [GradingModes.BY_STUDENT]: () => {
                unapprovedQuestions.length === 0
                    ? navigateToNextSubmission()
                    : dispatch(textSubmissionDrawerActions.setFocusedQuestionId(unapprovedQuestions[0].id))
            },
            [GradingModes.BY_RUBRIC]: () => {
                if (openedQuestion?.id === lastReviewedQuestionId) navigateToNextSubmission()
            },
            [GradingModes.BY_QUESTION]: () => {
                const isChildOfOpenedQuestion = openedQuestionParent?.children?.some(
                    question => question.id === lastReviewedQuestionId,
                )

                const isSameOrChildQuestion = lastReviewedQuestionId === openedQuestion?.id || isChildOfOpenedQuestion

                const isLastReviewedQuestionFromDifferentParent =
                    openedQuestionParent &&
                    !openedQuestionParent.children.some(question => question.id === lastReviewedQuestionId)

                if (!isSameOrChildQuestion || isLastReviewedQuestionFromDifferentParent) return

                unapprovedSiblingQuestions[0]
                    ? dispatch(textSubmissionDrawerActions.setFocusedQuestionId(unapprovedSiblingQuestions[0].id))
                    : navigateToNextSubmission()
            },
        }

        // gradingModeHandlers[gradingMode]?.()
        if (questionIdParam === 'all') gradingModeHandlers[GradingModes.BY_STUDENT]()
        else gradingModeHandlers[GradingModes.BY_QUESTION]()
    }

    const currentGroup = getCurrentGroup()
    const currentIndex = getSubmissionIndexInGroup(currentGroup)

    const previousSubmissionIds = getPreviousSubmissionIds(currentGroup, currentIndex, 1)
    const nextSubmissionIds = getNextSubmissionIds(currentGroup, currentIndex, 1)

    const previousSubmissionExists = checkIfPreviousSubmissionExists(currentGroup)
    const nextSubmissionExists = checkIfNextSubmissionExists(currentGroup)

    return {
        navigateToPreviousSubmission,
        navigateToNextSubmission,
        previousSubmissionExists,
        nextSubmissionExists,
        previousSubmission: previousSubmissionIds[0],
        nextSubmission: nextSubmissionIds[0],
        navigateToNextUnreviewedAnswer,
    }
}

export const useQuestionFocusNavigationShortcuts = () => {
    const focusedQuestionId = useSelector(textSubmissionDrawerSelectors.focusedQuestionId)
    const dispatch = useDispatch()
    const { token } = useParams()
    const versionId = useVersionId()
    const { data: questions } = useFetchQuestionsQuery({ token, versionId })

    useEffect(() => {
        const handleKeyDown = e => {
            if (e.altKey && e.key === 'ArrowDown') {
                const currentQuestionIndex = questions?.findIndex(question => question.id === focusedQuestionId)
                if (questions[currentQuestionIndex + 1]) {
                    dispatch(textSubmissionDrawerActions.setFocusedQuestionId(questions[currentQuestionIndex + 1].id))
                }
            }
            if (e.altKey && e.key === 'ArrowUp') {
                const currentQuestionIndex = questions?.findIndex(question => question.id === focusedQuestionId)
                if (questions[currentQuestionIndex - 1]) {
                    dispatch(textSubmissionDrawerActions.setFocusedQuestionId(questions[currentQuestionIndex - 1].id))
                }
            }
        }
        window.addEventListener('keydown', handleKeyDown)
        return () => window.removeEventListener('keydown', handleKeyDown)
    }, [focusedQuestionId, questions])
}

export const useScrollToFocusedQuestion = rescrollTrigger => {
    const focusedQuestionId = useSelector(textSubmissionDrawerSelectors.focusedQuestionId)
    const focusedQuestionIsExpanded = useSelector(textSubmissionDrawerSelectors.focusedQuestionIsExpanded)
    const dispatch = useDispatch()

    useEffect(() => {
        if (focusedQuestionId) {
            dispatch(textSubmissionDrawerActions.expandQuestion(focusedQuestionId))

            const timeoutId = setTimeout(
                () => {
                    const questionElement = document.querySelector(`.grading-card[data-id="${focusedQuestionId}"]`)
                    if (questionElement) {
                        questionElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
                    }
                },
                focusedQuestionIsExpanded ? 200 : 400,
            )

            return () => clearTimeout(timeoutId)
        }
    }, [focusedQuestionId, rescrollTrigger, dispatch])
}

export const useAutofocusOpenedQuestion = () => {
    const focusedQuestionId = useSelector(textSubmissionDrawerSelectors.focusedQuestionId)
    const openedSubmissionId = useSelector(submissionSelectors.openedSubmissionId)
    const dispatch = useDispatch()

    const { layerId: questionIdParam } = useParams()
    const questionId = Number(questionIdParam)

    // Focus the question if it's not already focused and valid
    useEffect(() => {
        if (isNil(focusedQuestionId) && isNumber(questionId) && !Number.isNaN(questionId)) {
            dispatch(textSubmissionDrawerActions.setFocusedQuestionId(questionId))
        }
    }, [questionId, dispatch])

    // Update the focused question whenever openedSubmissionId changes
    useEffect(() => {
        if (isNumber(questionId) && !Number.isNaN(questionId)) {
            dispatch(textSubmissionDrawerActions.setFocusedQuestionId(questionId))
        }
    }, [openedSubmissionId, questionId, dispatch])
}

const considerAsEscalated = escalated => {
    return [ExplainabilityStatuses.MANUAL_UNRESOLVED, ExplainabilityStatuses.UNEXPLAINABLE_UNRESOLVED].includes(
        escalated,
    )
}

export const useSelectNextEscalatedQuestion = (answerId, submissionId) => {
    const onlyEscalatedParam = useQueryParam(FLAGGED_PARAM) === 'true'
    const { data: submission } = useFetchTextSubmissionDetailsQuery({
        submissionId,
    })

    const dispatch = useDispatch()

    return () => {
        if (!onlyEscalatedParam) {
            return
        }

        for (const answer of submission.answers) {
            if (considerAsEscalated(answer.escalated) && answer.id != answerId) {
                dispatch(textSubmissionDrawerActions.setFocusedQuestionId(answer.questionId))

                break
            }
        }
    }
}

export const useScrollToFirstEscalatedOnMount = submissionId => {
    const onlyEscalatedParam = useQueryParam(FLAGGED_PARAM) === 'true'
    const { data: submission } = useFetchTextSubmissionDetailsQuery({
        submissionId,
    })
    const focusedQuestionId = useSelector(textSubmissionDrawerSelectors.focusedQuestionId)

    const dispatch = useDispatch()

    useEffect(() => {
        if (!onlyEscalatedParam || !submission?.id || (focusedQuestionId && !isNaN(focusedQuestionId))) {
            return
        }

        for (const answer of submission.answers) {
            if (considerAsEscalated(answer.escalated)) {
                dispatch(textSubmissionDrawerActions.setFocusedQuestionId(answer.questionId))

                break
            }
        }
    }, [submission?.id])
}

export const useAutoOpenSubmissionFromParams = groups => {
    const setQueryParams = useQueryParamSetter()
    const [openSubmissionId, focusQuestionId] = useQueryParams([OPEN_SUBMISSION_ID_PARAM, FOCUS_QUESTION_ID_PARAM])
    const dispatch = useDispatch()

    useEffect(() => {
        if (!groups?.length) {
            return
        }

        if (openSubmissionId) {
            dispatch(submissionActions.openSubmission({ id: +openSubmissionId, groups }))
        }

        if (focusQuestionId) {
            dispatch(textSubmissionDrawerActions.setFocusedQuestionId(+focusQuestionId))
        }

        if (openSubmissionId || focusQuestionId) {
            setQueryParams({ [OPEN_SUBMISSION_ID_PARAM]: null, [FOCUS_QUESTION_ID_PARAM]: null })
        }
    }, [openSubmissionId, focusQuestionId, groups])
}

export const useAutoViewMode = (submissionId) => {
    const dispatch = useDispatch()
    const viewMode = useSelector(textSubmissionDrawerSelectors.viewMode)
    const { data: submission } = useFetchTextSubmissionDetailsQuery({ submissionId })
    
    const [isParsedModeDisabled, setIsParsedModeDisabled] = useState(false)
    
    useEffect(() => {
        if (submission?.answers?.length) {
            const allAnswersEmpty = submission.answers.every(
                answer => !answer.content || answer.content.trim() === ''
            )
            
            setIsParsedModeDisabled(allAnswersEmpty)
            
            if (allAnswersEmpty && viewMode === ViewModes.PARSED) {
                dispatch(textSubmissionDrawerActions.setViewMode(ViewModes.PDF))
            }
        } else {
            setIsParsedModeDisabled(false)
        }
    }, [submission, dispatch, viewMode])
    
    return isParsedModeDisabled
}
